import React, { useState, useRef } from 'react'
import './FreeTrial.scss'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import Button from '../Button/Button'
import FormErrors from '../FormErrors/FormErrors'
import Link from '../Link/Link'
import ReCAPTCHA from 'react-google-recaptcha'
import { toTitleCase } from '../../functions/toTitleCase'
import { navigate } from 'gatsby'
import { AnimatePresence, motion } from 'framer-motion'

const RegisteredModal = ({message, visible, setVisible}) => {
	return (
		<AnimatePresence>
			{
				visible &&
				<div className="FormSuccessBox">
					<motion.div
						className="box"
						initial={{opacity: 0, y: -200}}
						animate={{opacity: 1, y: 0, transition: {delay: .2}}}
						exit={{opacity: 0}}
					>
						<button className="close-button" onClick={() => setVisible(false)}>
							Lukk
						</button>
						<div className="content">
							<h1>Forespørsel om prøvetilgang til Lovdata Pro er stoppet</h1>
							<p>Dette kan skyldes en av følgende årsaker:</p>
							<ul>
								<li>Prøvetilgang er benyttet på samme firma eller bruker tidligere.</li>
								<li>Det finnes allerede en aktiv avtale på samme organisasjonsnummer.</li>
								<li>E-postadressen er registret i Lovdata Pro som bruker.</li>
								<li>Det er skyldig utestående beløp fra et tidligere kundeforhold.</li>
							</ul>
							{ message && <p style={{color: 'red'}}>{message}</p> }
							<p>Kontakt oss på <a href="mailto:marked@lovdata.no">marked@lovdata.no</a> om du skulle lure på noe mer i henhold til informasjonen ovenfor.</p>
							<button onClick={() => setVisible(false)} className="Button primary">
								<span>Lukk</span>
							</button>
						</div>
					</motion.div>
					<motion.div
						className="bg"
						transition={{type: 'tween', duration: .3}}
						initial={{opacity: 0}}
						animate={{opacity: .96}}
						exit={{opacity: 0, transition: {delay: .2, type: 'tween', duration: .3}}}
						onClick={() => setVisible(false)}
						role="button"
					/>
				</div>
			}
		</AnimatePresence>
	)
}
export default function FreeTrial({ textbox, button, link, successText }) {

	const [loading, setLoading] = useState(false)
	const [formSent, setFormSent] = useState(false)
	const [registeredDetails, setRegisteredDetails] = useState('')
	const [visible, setVisible] = useState(false)
	const [isVerified, setIsVerified] = useState(false)
	const recaptchaRef = useRef(null)

	const { register, handleSubmit, formState: { errors }, setValue, setError } = useForm({
		mode: 'onSubmit',
		reValidate: 'onChange'
	})

	const onSubmit = async data => {
		if(isVerified) {
			setLoading(true)
			try {
				axios({
					method: 'post',
					url: '/.netlify/functions/mail-free-trial',
					data: data
				})
					.then(response => {
						setLoading(false)
						if (response.data.status === 'ok') {
							mailSent();
						}
						else if(response.data.status === 'registered') {
							setRegisteredDetails(response.data.details)
							setVisible(true)
						}
						else {
							console.log(response.data)
						}
					})

			} catch (err) {
				setLoading(false)
				console.log(err)
			}
		}
	}

	const mailSent = () => {
		navigate('/takk')
		setFormSent(true)
	}

	const searchCompany = e => {
		let orgNr = e.target.value.replace(/\s/g, '');
		setValue('orgNumber', orgNr)
		if (Math.floor(Math.log10(parseInt(orgNr + 1))) === 9) {
			fetch(`https://data.brreg.no/enhetsregisteret/api/enheter/${orgNr}`)
				.then(response => response.json())
				.then(response => {
					if (response) {
						if(response.navn) {
							setValue('companyName', toTitleCase(response.navn), { shouldValidate: true })
						}
					}
				})
				.catch(error => {
					setValue('companyName', '', { shouldValidate: false })
					setError('companyName', { type: 'custom', message: 'Finner ikke et firmanavn med gitt organisasjonsnummer' });
					console.log(error)
				})
		}
	}

	return (
		<div className="FreeTrial">
			<RegisteredModal message={registeredDetails} visible={visible} setVisible={setVisible} />
			<div className="form">
				{
					process.env.NODE_ENV === 'development' &&
					<>
						<button onClick={() => navigate('/takk')}>Toggle modal</button>
						<p>Jukse Orgnr: 818343752</p>
					</>
				}
				<h2 className="screen-reader-only">Fyll ut skjema</h2>
				{
					Object.keys(errors).length > 0 &&
					<FormErrors errors={errors} />
				}
				<form className="register-form" onSubmit={handleSubmit(onSubmit)}>

					<div className={errors.orgNumber ? 'field error' : 'field'}>
						{errors.orgNumber && <p className="errorMessage">{errors.orgNumber.message}</p>}
						<label htmlFor="orgNumber">Organisasjonsnummer <small>(9 siffer)</small></label>
						<input
							type="text"
							{...register('orgNumber', {
								required: "Du må fylle inn organisasjonsnummeret ditt",
								minLength: {
									value: 9,
									message: "Organisasjonsnummeret må være 9 siffer"
								},
								maxLength: {
									value: 9,
									message: "Organisasjonsnummeret må være 9 siffer"
								},
								pattern: {
									value: /^[0-9]*$/,
									message: "Organisasjonsnummer må kun være siffer"
								}
							})}
							id="orgNumber"
							onChange={searchCompany}
							/>
					</div>

					<div className={errors.companyName ? 'field error' : 'field'}>
						{errors.companyName && <p className="errorMessage">{errors.companyName.message}</p>}
						<label htmlFor="companyName">Firmanavn</label>
						<input
							type="text"
							{...register('companyName', { required: 'Finner ikke gyldig firmanavn' })}
							placeholder={"Firmanavn hentes automatisk"}
							readOnly
							id="companyName"
							/>
					</div>

					<div className="doubleFields">
						<div className={errors.contactFirstName ? 'field error' : 'field'}>
							{errors.contactFirstName && <p className="errorMessage">{errors.contactFirstName.message}</p>}
							<label htmlFor="contactFirstName">Fornavn</label>
							<input
								type="text"
								{...register('contactFirstName', {
									required: "Du må fylle inn fornavn",
									minLength: {
										value: 2,
										message: "Navnet du har skrevet inn er for kort"
									},
									pattern: {
										value: /^([^0-9]*)$/,
										message: "Du kan ikke bruke tall i navnet"
									}
								})}
								id="contactFirstName"
								autoComplete="given-name" />
						</div>

						<div className={errors.contactLastName ? 'field error' : 'field'}>
							{errors.contactLastName && <p className="errorMessage">{errors.contactLastName.message}</p>}
							<label htmlFor="contactLastName">Etternavn</label>
							<input
								type="text"
								{...register('contactLastName', {
									required: "Du må fylle inn etternavn",
									minLength: {
										value: 2,
										message: "Navnet du har skrevet inn er for kort"
									},
									pattern: {
										value: /^([^0-9]*)$/,
										message: "Du kan ikke bruke tall i etternavnet"
									}
								})}
								id="contactLastName"
								autoComplete="family-name" />
						</div>
					</div>

					<div className={errors.phone ? 'field error' : 'field'}>
						{errors.phone && <p className="errorMessage">{errors.phone.message}</p>}
						<label htmlFor="phone">Telefonnummer <small>(minst 8 siffer)</small></label>
						<input
							type="tel"
							{...register('phone', {
								required: "Du må fylle inn telefonnummeret ditt",
								minLength: {
									value: 8,
									message: "Telefonnummeret er for kort, det må være minst 8 siffer"
								},
								maxLength: {
									value: 15,
									message: "Telefonnummeret er for langt, det må være maks 15 siffer"
								},
								pattern: {
									value: /^[0-9]*$/,
									message: "Telefonnummer må kun være siffer"
								}
							})}
							id="phone"
							autoComplete="tel" />
					</div>

					<div className={errors.email ? 'field error' : 'field'}>
						{errors.email && <p className="errorMessage">{errors.email.message}</p>}
						<label htmlFor="email">Epost</label>
						<input
							type="email"
							{...register('email', {
								required: "Du må fylle inn eposten din",
								pattern: {
									value: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
									message: "Ugyldig epostadresse"
								}
							})}
							id="email"
							autoComplete="email" />
					</div>

					<div>
						<div className="form-checkbox field">
							{errors.termsCheckbox && <p className="errorMessage">{errors.termsCheckbox.message}</p>}
							<label htmlFor="termsCheckbox">
								<input
									id="termsCheckbox"
									type="checkbox"
									{...register(
										'termsCheckbox',
										{ required: 'Du må akseptere personvernerklæringen før du sender inn bestilling' },
									)} />
								<span className="box" />
								Jeg har lest og akseptert <a target="_blank" href="/personvern">personvernerklæringen</a>
							</label>
						</div>
					</div>
					{
						isVerified
						? <button disabled={formSent} className={`Button blue_dark loader ${loading ? 'loading' : ''}`} type="submit"><span>Send inn</span></button>
						: <ReCAPTCHA
								ref={recaptchaRef}
								sitekey={`${process.env.GATSBY_RECAPTCHA_KEY}`}
								onChange={() => setTimeout(() => setIsVerified(true), 500)}
							/>
					}
				</form>
			</div>
			<div className="textbox">
				<div className="box" dangerouslySetInnerHTML={{ __html: textbox }} />
				<div className="links">
					{button && <Button {...button} color="primary-white" />}
					{
						link &&
						<div className="link">
							<Link {...link} type="arrow" />
						</div>
					}
				</div>
			</div>
		</div>
	);
}